<template>
    <div class="map-wrapper">
        <div id="public-map" class="map"></div>
        <HtmlModalMap ref="modalRef" @hide="closePopover" />
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import axios from 'axios'
import Map from 'ol/Map'
import View from 'ol/View'
import { defaults as defaultControlsOL } from 'ol/control/defaults'
import { baseLayers } from '../../../open-layers/baseLayers'
import { fullScreenBtn, layerSwitcher } from '../../../open-layers/default-controls'
import { applyViewerLayerSwitcherDefaults, buildStyleFunctionFromBackend, createLayerToViewer, mapsLayerTypeValue } from '../../../open-layers/helpers'
import { getCenter } from 'ol/extent'
import { toLonLat } from 'ol/proj'
import Select from 'ol/interaction/Select'
import HtmlModalMap from '../MapsOpenLayers/Components/HtmlModalMap.vue'
import { getDefaultMapView } from '../../../open-layers/map-defaults.js'

const props = defineProps({
    id: { type: [Number, String], required: true },
    isThematic: { type: Boolean, default: false },
})

const layerId = computed(() => props.id)
const isThematicLayer = computed(() => props.isThematic)
const mapRef = ref(null)
const modalRef = ref(null)
const olLayerRef = ref(null)
const activePopoverCoordinate = ref(null)

let disposeMap = null

const getLayer = async () => {
    const type = isThematicLayer.value ? 'thematic-layers' : 'layers'
    const { data } = await window.axios.get(`/api/maps/public/${type}/${layerId.value}`)
    return data.data
}

function substituteAttributeTemplate(template, props) {
    if (!template) return ''
    const p = props && typeof props === 'object' ? props : {}
    return String(template).replace(/\$([a-zA-Z0-9_]+)/g, (_, key) => (p[key] != null ? String(p[key]) : ''))
}

const closePopover = () => {
    activePopoverCoordinate.value = null
}

const openDetailsByFeatureTematic = (feature) => {
    if (!feature) {
        modalRef.value?.closePopover?.()
        activePopoverCoordinate.value = null
        return
    }
    const map = mapRef.value
    if (!map) return
    const geometry = feature.getGeometry()
    if (!geometry) return
    const extent = geometry.getExtent()
    const center = getCenter(extent)
    activePopoverCoordinate.value = center
    const pixel = map.getPixelFromCoordinate(center)
    const reference = feature.get('reference')
    modalRef.value?.openPopover?.(
        { html: reference ? reference.replaceAll('\n', '<br/>') : null },
        pixel
    )
}

onMounted(async () => {
    const layer = await getLayer()

    if (isThematicLayer.value) {
        layer.layer_type = 'VectorLayer'
        layer.layer_source = 'Vector'
        layer.geo_table = 'thematic_maps.thematic_' + layerId.value
    }
    layer.__public_request = true

    applyViewerLayerSwitcherDefaults(layer)

    layer.style = buildStyleFunctionFromBackend(layer)
    const layerOpenLayers = await createLayerToViewer(layer)
    olLayerRef.value = layerOpenLayers

    const map = new Map({
        target: 'public-map',
        layers: [baseLayers(), layerOpenLayers],
        view: new View(getDefaultMapView()),
        controls: defaultControlsOL().extend([layerSwitcher, fullScreenBtn]),
    })

    mapRef.value = map

    const onMoveEndPopover = () => {
        if (activePopoverCoordinate?.value && modalRef?.value?.updatePopoverPosition) {
            const pixel = map.getPixelFromCoordinate(activePopoverCoordinate.value)
            modalRef.value.updatePopoverPosition(pixel)
        }
    }

    const disposers = []

    if (isThematicLayer.value) {
        const selectInteraction = new Select({ layers: [layerOpenLayers] })
        selectInteraction.on('select', (event) => {
            const feature = event.selected?.[0] ?? null
            if (!feature) return

            let props = feature.getProperties()
            delete props.geometry

            const lyr = event.target.getLayer(feature)
            const template = lyr?.get('reference') || ''

            const reference = template.replace(/\$([a-zA-Z0-9_]+)/g, (match, key) => props[key] ?? '')
            feature.set('reference', reference)

            openDetailsByFeatureTematic(feature)
        })
        map.addInteraction(selectInteraction)
        disposers.push(() => map.removeInteraction(selectInteraction))
        map.on('moveend', onMoveEndPopover)
        disposers.push(() => map.un('moveend', onMoveEndPopover))
    } else {
        const templateHtml = layer.attribute_display_html
        const isPersonalizado =
            String(layer.action ?? '') === 'Personalizado' && templateHtml && String(templateHtml).trim().length > 0

        if (isPersonalizado) {
            const openHtmlPopoverFromTemplate = (props, coordinate) => {
                const template = olLayerRef.value?.get('reference') || templateHtml
                const html = substituteAttributeTemplate(template, props)
                if (!html) {
                    modalRef.value?.closePopover?.()
                    activePopoverCoordinate.value = null
                    return
                }
                activePopoverCoordinate.value = coordinate
                const pixel = map.getPixelFromCoordinate(coordinate)
                modalRef.value?.openPopover?.(
                    { html: html.replaceAll('\n', '<br/>') },
                    pixel
                )
            }

            const isVectorConfig = mapsLayerTypeValue(layer) === 'VectorLayer'

            if (isVectorConfig) {
                const selectPersonalizado = new Select({ layers: [layerOpenLayers] })
                selectPersonalizado.on('select', async (event) => {
                    const feature = event.selected?.[0] ?? null
                    if (!feature) {
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                        return
                    }
                    const gid = Number(feature.get('gid'))
                    if (!Number.isFinite(gid) || gid < 1) {
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                        return
                    }
                    try {
                        const { data: resp } = await axios.post(
                            `/api/maps/public/layers/${layerId.value}/feature-by-gid`,
                            { gid }
                        )
                        const props = resp?.data
                        if (!props || typeof props !== 'object') {
                            modalRef.value?.closePopover?.()
                            activePopoverCoordinate.value = null
                            return
                        }
                        const geom = feature.getGeometry()
                        const center = geom ? getCenter(geom.getExtent()) : map.getView().getCenter()
                        openHtmlPopoverFromTemplate(props, center)
                    } catch (e) {
                        console.warn('Consulta por gid (visualização pública) falhou:', e)
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                    }
                })
                map.addInteraction(selectPersonalizado)
                disposers.push(() => map.removeInteraction(selectPersonalizado))
                map.on('moveend', onMoveEndPopover)
                disposers.push(() => map.un('moveend', onMoveEndPopover))
            } else {
                const onSingleClickPersonalizado = async (evt) => {
                    const [lon, lat] = toLonLat(evt.coordinate)
                    try {
                        const { data: resp } = await axios.post(
                            `/api/maps/public/layers/${layerId.value}/feature-at-point`,
                            { lon, lat }
                        )
                        const props = resp?.data
                        if (!props || typeof props !== 'object') {
                            modalRef.value?.closePopover?.()
                            activePopoverCoordinate.value = null
                            return
                        }
                        openHtmlPopoverFromTemplate(props, evt.coordinate)
                    } catch (e) {
                        console.warn('Consulta de atributos (visualização pública) falhou:', e)
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                    }
                }

                map.on('singleclick', onSingleClickPersonalizado)
                disposers.push(() => map.un('singleclick', onSingleClickPersonalizado))
                map.on('moveend', onMoveEndPopover)
                disposers.push(() => map.un('moveend', onMoveEndPopover))
            }
        }
    }

    disposeMap = () => {
        disposers.forEach((fn) => {
            try {
                fn()
            } catch (_) {}
        })
        disposers.length = 0
    }
})

onUnmounted(() => {
    disposeMap?.()
    disposeMap = null
})
</script>

<style scoped>
.map-wrapper { width: 100%; height: 100vh; position: relative; }
.map { width: 100%; height: 100%; border-radius: 8px; overflow: hidden; }
</style>
